import {
  AccountBalance,
  AttachMoney,
  CheckCircle,
  Payment,
} from '@mui/icons-material';
import {
  Alert,
  AlertTitle,
  Box,
  Chip,
  Grid,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { getSGRWarrantyRequestPayments } from 'lib/api/sgr';
import AlertBanner from 'lib/designSystem/AlertBanner';
import TableWithPagination from 'lib/designSystem/TableWithPagination';
import { Header } from 'lib/designSystem/TableWithPagination';
import { errorHandler } from 'lib/functions/errors';
import { formatDate, formatMoney } from 'lib/functions/utils';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

interface Response {
  amount: number;
  amountConciliated: number;
  paidAt: string;
  createdAt: string;
  conciliations: PaymentConciliation[];
}

interface PaymentConciliation {
  id: number;
  documentId: string;
  amount: number;
  companyId: number;
  payInId: number;
  createdAt: string;
  payIn: {
    id: number;
    amount: number;
    status: string;
    bankAccountOriginId: number;
    bankAccountDestinationId: number;
    companyId: number;
    createdAt: string;
    bankAccountOrigin: {
      id: number;
      bankName: string;
      bankId: number;
      holderName: string;
      holderIdentifier: string;
      accountNumber: string;
      type: string;
      businessIdentifier: string;
      currency: string;
      selected: boolean;
      companyId: number;
      email: string | null;
      powerDueDate: string | null;
    };
  };
  document: {
    businessIdentifier: string;
    stakeholderIdentifier: string;
  };
  payInDate: string;
  businessIdentifier: string;
  typeEsp: string;
  requestId: number;
  type: string;
  paidBy: string;
  paymentType: string;
}

interface Payment {
  paymentAmount: number;
  amountConciliated: number;
  bankName: string;
  paidBy: string;
  holderIdentifier: string;
  accountNumber: string;
  bankAccountType: string;
  currency: string;
  holderEmail: string | null;
  createdAt: string;
  paymentType: string;
}

const PaymentsSkeleton: React.FC = () => {
  return (
    <Box mb={2}>
      <Paper
        sx={{
          p: 1.5,
          boxShadow: 'none',
          border: '1px solid #e0e0e0',
          mb: 2,
          borderRadius: 2,
        }}
      >
        <Skeleton variant="text" width="60%" height={24} sx={{ mb: 1.5 }} />
        <Grid container spacing={2} sx={{ mb: 1.5 }}>
          {[1, 2, 3].map((i) => (
            <Grid item xs={12} md={4} key={i}>
              <Skeleton
                variant="rectangular"
                height={40}
                sx={{ borderRadius: 1 }}
              />
            </Grid>
          ))}
        </Grid>
        <Skeleton variant="rectangular" height={30} sx={{ borderRadius: 1 }} />
      </Paper>

      <Paper
        sx={{
          p: 1.5,
          boxShadow: 'none',
          border: '1px solid #e0e0e0',
          borderRadius: 2,
        }}
      >
        <Skeleton variant="text" width="40%" height={20} sx={{ mb: 1.5 }} />
        <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 1 }} />
      </Paper>
    </Box>
  );
};

const Payments: React.FC<{ warrantyId: number }> = ({ warrantyId }) => {
  const dispatch = useDispatch();
  const [payments, setPayments] = useState<Payment[]>([]);
  const [summary, setSummary] = useState<{
    amount: number;
    amountConciliated: number;
    paidAt: string;
    createdAt: string;
  } | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchPayments = async () => {
    try {
      setLoading(true);
      const response: Response[] = await getSGRWarrantyRequestPayments(
        dispatch,
        warrantyId,
      );

      const data = response[0];

      if (data.conciliations) {
        const paymentsWithType = data.conciliations.map((payment) => ({
          id: payment.id,
          paymentAmount: payment.payIn.amount,
          amountConciliated: payment.amount,
          bankName: payment.payIn.bankAccountOrigin.bankName,
          paidBy: payment.payIn.bankAccountOrigin.holderName,
          holderIdentifier: payment.payIn.bankAccountOrigin.holderIdentifier,
          accountNumber: payment.payIn.bankAccountOrigin.accountNumber,
          bankAccountType: payment.payIn.bankAccountOrigin.type,
          currency: payment.payIn.bankAccountOrigin.currency,
          holderEmail: payment.payIn.bankAccountOrigin.email,
          createdAt: payment.payIn.createdAt,
          paymentType: 'transferencia',
        }));
        setPayments(paymentsWithType);
      } else {
        setPayments([]);
      }

      setSummary({
        amount: data.amount,
        amountConciliated: data.amountConciliated,
        paidAt: data.paidAt,
        createdAt: data.createdAt,
      });
    } catch (error) {
      errorHandler(dispatch, error, 'Error al cargar los pagos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchPayments();
  }, [dispatch, warrantyId]);

  if (loading) return <PaymentsSkeleton />;

  if (!summary)
    return (
      <AlertBanner
        severity="info"
        title="No hay información de pagos disponible"
        message="No se encontraron datos de conciliación de pagos para esta garantía."
      />
    );

  const remainingAmount = summary.amount - summary.amountConciliated;
  const isFullyPaid = remainingAmount <= 0;

  const tableHeaders: Header[] = [
    {
      label: 'ID',
      key: 'id',
      type: 'numeric-id',
    },
    {
      label: 'Monto',
      key: 'paymentAmount',
      type: 'money',
    },
    {
      label: 'Conciliado',
      key: 'amountConciliated',
      type: 'money',
    },
    {
      label: 'Banco Origen',
      key: 'bankName',
    },
    {
      label: 'Pagado Por',
      key: 'paidBy',
    },
    {
      label: 'Fecha Conciliación',
      key: 'createdAt',
      type: 'date',
    },
    {
      label: 'Tipo',
      key: 'paymentType',
      customComponent: (value: string) => (
        <Chip
          label={value}
          size="small"
          color="primary"
          variant="outlined"
          sx={{ height: '20px', fontSize: '0.65rem' }}
        />
      ),
    },
  ];

  return (
    <Box>
      <Paper
        sx={{
          p: 1.5,
          boxShadow: 'none',
          border: '1px solid #e0e0e0',
          mb: 2,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 1,
            mb: 1.5,
          }}
        >
          <Box
            sx={{
              backgroundColor: 'primary.main',
              borderRadius: '50%',
              p: 0.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
            }}
          >
            <Payment sx={{ fontSize: '1rem' }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: 'text.primary',
                fontSize: '1rem',
                lineHeight: 1.1,
              }}
            >
              Conciliación de Pagos
            </Typography>
            <Stack direction="row" spacing={0.5}>
              <Chip
                label="Costo de emisión"
                size="small"
                color="primary"
                variant="outlined"
                sx={{ height: '18px', fontSize: '0.65rem' }}
              />
              <Chip
                label={
                  isFullyPaid ? 'Completamente pagado' : 'Parcialmente pagado'
                }
                size="small"
                color={isFullyPaid ? 'success' : 'warning'}
                sx={{ height: '18px', fontSize: '0.65rem' }}
              />
            </Stack>
          </Box>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Box display="flex" alignItems="center" gap={1}>
              <Box
                sx={{
                  backgroundColor: 'rgba(25, 118, 210, 0.1)',
                  borderRadius: '50%',
                  p: 0.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <AttachMoney
                  sx={{ fontSize: '0.8rem', color: 'primary.main' }}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: '0.65rem' }}
                >
                  Total a Conciliar
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight="medium"
                  sx={{ fontSize: '0.75rem' }}
                >
                  {formatMoney(summary.amount)} CLP
                </Typography>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} md={4}>
            <Box display="flex" alignItems="center" gap={1}>
              <Box
                sx={{
                  backgroundColor: 'rgba(76, 175, 80, 0.1)',
                  borderRadius: '50%',
                  p: 0.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <CheckCircle
                  sx={{ fontSize: '0.8rem', color: 'success.main' }}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: '0.65rem' }}
                >
                  Total Conciliado
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight="medium"
                  sx={{ fontSize: '0.75rem' }}
                >
                  {formatMoney(summary.amountConciliated)} CLP
                </Typography>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} md={4}>
            <Box display="flex" alignItems="center" gap={1}>
              <Box
                sx={{
                  backgroundColor: 'rgba(0, 150, 136, 0.1)',
                  borderRadius: '50%',
                  p: 0.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <AccountBalance
                  sx={{ fontSize: '0.8rem', color: 'info.main' }}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: '0.65rem' }}
                >
                  Fecha de Pago
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight="medium"
                  sx={{ fontSize: '0.75rem' }}
                >
                  {summary.paidAt
                    ? formatDate(new Date(summary.paidAt))
                    : 'Pendiente'}
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>

        {!isFullyPaid && (
          <Alert
            severity="warning"
            sx={{
              mt: 2,
              borderRadius: 0.5,
              backgroundColor: 'rgba(255, 152, 0, 0.08)',
              border: '1px solid rgba(255, 152, 0, 0.2)',
            }}
          >
            <AlertTitle sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
              Monto Pendiente
            </AlertTitle>
            <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
              Falta por conciliar: {formatMoney(remainingAmount)} CLP
            </Typography>
          </Alert>
        )}
      </Paper>

      {payments.length > 0 && (
        <Paper
          sx={{
            p: 1.5,
            boxShadow: 'none',
            border: '1px solid #e0e0e0',
            mb: 2,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 1,
              mb: 1.5,
            }}
          >
            <Box
              sx={{
                backgroundColor: 'rgba(76, 175, 80, 0.1)',
                borderRadius: '50%',
                p: 0.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Payment sx={{ fontSize: '0.8rem', color: 'success.main' }} />
            </Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: 'text.primary',
                fontSize: '1rem',
                lineHeight: 1.1,
              }}
            >
              Pagos Utilizados para Conciliar
            </Typography>
            <Chip
              label={`${payments.length} pago${payments.length !== 1 ? 's' : ''}`}
              size="small"
              color="success"
              variant="outlined"
              sx={{ height: '18px', fontSize: '0.65rem' }}
            />
          </Box>

          <TableWithPagination
            data={payments}
            setData={setPayments}
            dataHeaders={tableHeaders}
            tableSize="small"
            pagination={{
              total: payments.length,
              page: 1,
              pageSize: 10,
              totalPages: Math.ceil(payments.length / 10),
            }}
            setPagination={() => {}}
            searchData={() => Promise.resolve()}
            setLoading={() => {}}
            exportToFile="xlsx"
            exportComplete
          />
        </Paper>
      )}
    </Box>
  );
};

export default Payments;
export { PaymentsSkeleton };
